// src/services/protocolApi.js
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:7600/api';
const BASE_URL = backendUrl.endsWith('/api')
  ? `${backendUrl}/protocol-management`
  : `${backendUrl}/api/protocol-management`;

// RTK Query API
export const protocolApi = createApi({
  reducerPath: 'protocolApi',
  baseQuery: fetchBaseQuery({ 
    baseUrl: BASE_URL,
    prepareHeaders: (headers) => {
      const token = localStorage.getItem('token') || localStorage.getItem('authToken');
      if (token) headers.set('Authorization', `Bearer ${token}`);
      return headers;
    }
  }),
  tagTypes: ['Protocols', 'RawPackets', 'Stats'],
  endpoints: (builder) => ({
    // ============ PROTOCOLS ============
    
    // Get all protocols
    getProtocols: builder.query({
      query: (filters = {}) => {
        const params = new URLSearchParams();
        if (filters.status) params.append('status', filters.status);
        if (filters.manufacturer) params.append('manufacturer', filters.manufacturer);
        const queryString = params.toString();
        return `/protocols${queryString ? `?${queryString}` : ''}`;
      },
      providesTags: ['Protocols']
    }),

    // Get single protocol
    getProtocol: builder.query({
      query: (protocolId) => `/protocols/${protocolId}`,
      providesTags: (result, error, protocolId) => [{ type: 'Protocols', id: protocolId }]
    }),

    // Create protocol
    createProtocol: builder.mutation({
      query: (data) => ({
        url: '/protocols',
        method: 'POST',
        body: data
      }),
      invalidatesTags: ['Protocols', 'Stats']
    }),

    // Update protocol
    updateProtocol: builder.mutation({
      query: ({ protocolId, ...data }) => ({
        url: `/protocols/${protocolId}`,
        method: 'PUT',
        body: data
      }),
      invalidatesTags: ['Protocols', 'Stats']
    }),

    // Delete protocol
    deleteProtocol: builder.mutation({
      query: (protocolId) => ({
        url: `/protocols/${protocolId}`,
        method: 'DELETE'
      }),
      invalidatesTags: ['Protocols', 'Stats']
    }),

    // Test protocol
    testProtocol: builder.mutation({
      query: ({ protocolId, rawHex }) => ({
        url: '/protocols/test',
        method: 'POST',
        body: { protocolId, rawHex }
      })
    }),

    // Runtime adapters currently available in server code
    getRuntimeProtocols: builder.query({
      query: () => '/runtime/protocols',
      providesTags: ['Protocols']
    }),

    // Test against live runtime parser/adapter path
    testRuntimeProtocol: builder.mutation({
      query: ({ runtimeProtocol, rawHex, protocolId, imei }) => ({
        url: '/protocols/runtime-test',
        method: 'POST',
        body: { runtimeProtocol, rawHex, protocolId, imei }
      })
    }),

    // ============ RAW PACKETS ============
    
    // Get unreviewed packets
    getUnreviewedPackets: builder.query({
      query: ({ page = 1, limit = 20 }) => 
        `/raw-packets/unreviewed?page=${page}&limit=${limit}`,
      providesTags: ['RawPackets']
    }),

    // Analyze packet
    analyzePacket: builder.query({
      query: (packetId) => `/raw-packets/${packetId}/analyze`
    }),

    // Review packet
    reviewPacket: builder.mutation({
      query: ({ packetId, ...data }) => ({
        url: `/raw-packets/${packetId}/review`,
        method: 'POST',
        body: data
      }),
      invalidatesTags: ['RawPackets', 'Stats']
    }),

    // ============ STATS ============
    
    // Get stats
    getStats: builder.query({
      query: () => '/stats',
      providesTags: ['Stats']
    })
  })
});

// Export hooks
export const { 
  // Protocols
  useGetProtocolsQuery,
  useGetProtocolQuery,
  useLazyGetProtocolQuery,
  useCreateProtocolMutation,
  useUpdateProtocolMutation,
  useDeleteProtocolMutation,
  useTestProtocolMutation,
  useGetRuntimeProtocolsQuery,
  useTestRuntimeProtocolMutation,
  
  // Raw Packets
  useGetUnreviewedPacketsQuery,
  useAnalyzePacketQuery,
  useLazyAnalyzePacketQuery,
  useReviewPacketMutation,
  
  // Stats
  useGetStatsQuery
} = protocolApi;

export default protocolApi;
